// Grid geometry of tile interiors: sides, rotation of machine footprints and the sockets
// through which belts, machines and ports hand material to their neighbours.
import type { Cell, Side } from '../world/island';

export type { Cell, Side };

/** Quarter turns clockwise, as the player rotates a machine. */
export type Rotation = 0 | 1 | 2 | 3;

export const SIDES: readonly Side[] = ['N', 'E', 'S', 'W'];

const STEP: Readonly<Record<Side, Cell>> = { N: [0, -1], E: [1, 0], S: [0, 1], W: [-1, 0] };
const OPPOSITE: Readonly<Record<Side, Side>> = { N: 'S', E: 'W', S: 'N', W: 'E' };

export const opposite = (side: Side): Side => OPPOSITE[side];

/** The cell across `side` of `cell`. */
export function neighbour([col, row]: Cell, side: Side): Cell {
  const [dc, dr] = STEP[side];
  return [col + dc, row + dr];
}

/** The side of `from` that faces the orthogonally adjacent cell `to`, if they touch. */
export function sideTowards([c1, r1]: Cell, [c2, r2]: Cell): Side | undefined {
  return SIDES.find((side) => STEP[side][0] === c2 - c1 && STEP[side][1] === r2 - r1);
}

export function rotateSide(side: Side, rotation: Rotation): Side {
  return SIDES[(SIDES.indexOf(side) + rotation) % 4] ?? side;
}

export const isRotation = (value: unknown): value is Rotation => value === 0 || value === 1 || value === 2 || value === 3;

/** Width and height of a footprint after rotating it. */
export function rotatedSize([w, h]: Cell, rotation: Rotation): Cell {
  return rotation % 2 === 0 ? [w, h] : [h, w];
}

/** Position of a footprint cell after rotating the footprint clockwise about its own box. */
export function rotateLocal([x, y]: Cell, [w, h]: Cell, rotation: Rotation): Cell {
  switch (rotation) {
    case 0:
      return [x, y];
    case 1:
      return [h - 1 - y, x];
    case 2:
      return [w - 1 - x, h - 1 - y];
    case 3:
      return [y, w - 1 - x];
  }
}

/** Every cell a footprint of `size` covers when its top-left corner is at `origin`. */
export function footprint(origin: Cell, size: Cell, rotation: Rotation): Cell[] {
  const [w, h] = rotatedSize(size, rotation);
  const cells: Cell[] = [];
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) cells.push([origin[0] + x, origin[1] + y]);
  return cells;
}

/** A crossing point on a cell edge: material leaves (or enters) `cell` through `side`. */
export interface Socket {
  readonly cell: Cell;
  readonly side: Side;
}

/** Places a machine's local socket in the interior, given the machine's origin and rotation. */
export function placeSocket(local: Socket, origin: Cell, size: Cell, rotation: Rotation): Socket {
  const [x, y] = rotateLocal(local.cell, size, rotation);
  return { cell: [origin[0] + x, origin[1] + y], side: rotateSide(local.side, rotation) };
}

/**
 * The receiving end of an output socket: the cell beyond the edge and the side it is entered from.
 * Material leaving `cell` through `side` enters the neighbour through the opposite side.
 */
export function across(socket: Socket): Socket {
  return { cell: neighbour(socket.cell, socket.side), side: opposite(socket.side) };
}

export const keyOf = ([col, row]: Cell): string => `${col},${row}`;

/** Sorting key that keeps cells in reading order (row, then column) whatever order they were built in. */
export function readingOrder(key: string): number {
  const [col = 0, row = 0] = key.split(',').map(Number);
  return row * 10_000 + col;
}

/** Numeric part of ids like `m12`, so machines and routes sort in creation order. */
export const idOrder = (id: string): number => Number(id.replace(/\D/g, '')) || 0;

export const inBounds = ([col, row]: Cell, [w, h]: Cell): boolean => col >= 0 && row >= 0 && col < w && row < h;

/**
 * The interior cell a port on the tile border sits next to, and the side of that cell on the border.
 * `index` counts along the side from the top (W, E) or from the left (N, S).
 */
export function portEdge(side: Side, index: number, [w, h]: Cell): Socket {
  switch (side) {
    case 'N':
      return { cell: [index, 0], side };
    case 'S':
      return { cell: [index, h - 1], side };
    case 'W':
      return { cell: [0, index], side };
    case 'E':
      return { cell: [w - 1, index], side };
  }
}
